import { Search, MessageCircle, Calendar, Shield, Star, Headphones } from "lucide-react";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

// Animated counter component
function AnimatedCounter({ target, duration = 2000 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const { ref, isInView } = useInView<HTMLSpanElement>({ threshold: 0.5 });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(progress * target);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isInView, target, duration]);

  return <span ref={ref}>{count.toFixed(1)}</span>;
}

// Animated stars component
function AnimatedStars({ count, isInView }: { count: number; isInView: boolean }) {
  return (
    <div className="flex justify-center gap-1 mb-2">
      {Array.from({ length: count }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            "w-5 h-5 fill-current transition-all duration-500",
            isInView ? "opacity-100 scale-100" : "opacity-0 scale-0"
          )}
          style={{ transitionDelay: `${i * 150 + 500}ms` }}
        />
      ))}
    </div>
  );
}

const testimonials = [
  {
    name: "Camila R.",
    pet: "Luna, Golden Retriever",
    text: "Minha Luna ficou super feliz! Recebi fotos e vídeos todos os dias. Voltou mais gordinha de tão bem tratada!",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
  },
  {
    name: "Roberto S.",
    pet: "Thor, Labrador",
    text: "Excelente experiência! O Thor é um cachorro grande e encontrou espaço perfeito no sítio. Recomendo demais!",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
  },
  {
    name: "Juliana M.",
    pet: "Mia, Gata Siamês",
    text: "A Mia é muito arredia, mas a Patricia soube conquistá-la. Fiquei tranquila durante toda a viagem.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
  },
];

export function TrustSections() {
  const { ref: howItWorksRef, isInView: howItWorksInView } = useInView<HTMLDivElement>();
  const { ref: securityRef, isInView: securityInView } = useInView<HTMLDivElement>();
  const { ref: testimonialsRef, isInView: testimonialsInView } = useInView<HTMLDivElement>();

  return (
    <div className="space-y-16">
      {/* How it Works */}
      <section className="py-12">
        <div className="container" ref={howItWorksRef}>
          <div className="text-center mb-10">
            <h2 
              className={cn(
                "text-2xl md:text-3xl font-bold mb-4 transition-all duration-700",
                howItWorksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
            >
              Como funciona
            </h2>
            <p 
              className={cn(
                "text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200",
                howItWorksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
            >
              Somos uma plataforma que tem como objetivo unir tutores e anfitriões de forma segura e prática.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto relative">
            {/* Connection line (hidden on mobile) */}
            <div className="hidden md:block absolute top-8 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-forest/20 via-forest to-forest/20">
              <div 
                className={cn(
                  "h-full bg-forest transition-all duration-1000 ease-out",
                  howItWorksInView ? "w-full" : "w-0"
                )}
                style={{ transitionDelay: "0.5s" }}
              />
            </div>

            {[
              { icon: Search, title: "1. Busque", desc: "Encontre anfitriões verificados perto de você com os serviços que precisa." },
              { icon: MessageCircle, title: "2. Converse", desc: "Tire suas dúvidas diretamente com o anfitrião antes de reservar." },
              { icon: Calendar, title: "3. Reserve", desc: "Agende com segurança e acompanhe seu pet durante toda a estadia." },
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <div 
                  key={i}
                  className={cn(
                    "text-center relative z-10 transition-all duration-700",
                    howItWorksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                  )}
                  style={{ transitionDelay: `${i * 200 + 300}ms` }}
                >
                  <div 
                    className={cn(
                      "w-16 h-16 rounded-full bg-forest-light mx-auto mb-4 flex items-center justify-center",
                      "transition-all duration-500 hover:scale-110 hover:shadow-lg hover:bg-forest/20",
                      "group cursor-pointer",
                      howItWorksInView && "animate-bounce-soft"
                    )}
                    style={{ animationDelay: `${i * 200}ms`, animationDuration: "2s" }}
                  >
                    <Icon className="w-8 h-8 text-forest group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="py-12 bg-forest text-white">
        <div className="container" ref={securityRef}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <h2 
                className={cn(
                  "text-2xl md:text-3xl font-bold mb-6 transition-all duration-700",
                  securityInView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
                )}
              >
                Segurança e suporte em primeiro lugar
              </h2>
              <ul className="space-y-4">
                {[
                  { icon: Shield, title: "Anfitriões verificados", desc: "Todos passam por verificação de identidade e perfil." },
                  { icon: Star, title: "Avaliações reais", desc: "Veja o que outros tutores acharam da experiência." },
                  { icon: Headphones, title: "Suporte 24h", desc: "Nossa equipe está sempre disponível para ajudar." },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <li 
                      key={i}
                      className={cn(
                        "flex items-start gap-3 transition-all duration-700",
                        securityInView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
                      )}
                      style={{ transitionDelay: `${i * 150 + 200}ms` }}
                    >
                      <div className="p-2 bg-white/10 rounded-lg animate-floating" style={{ animationDelay: `${i * 0.5}s` }}>
                        <Icon className="w-6 h-6 flex-shrink-0" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{item.title}</h4>
                        <p className="text-white/80 text-sm">{item.desc}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div 
              className={cn(
                "bg-white/10 rounded-2xl p-6 backdrop-blur-sm transition-all duration-700",
                "hover:bg-white/15 hover:scale-105",
                securityInView ? "opacity-100 translate-x-0 rotate-0" : "opacity-0 translate-x-12 rotate-3"
              )}
              style={{ transitionDelay: "0.3s" }}
            >
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">
                  <AnimatedCounter target={5.0} />
                </div>
                <AnimatedStars count={5} isInView={securityInView} />
                <p className="text-white/80 text-sm">
                  Média de avaliações de mais de 10.000 estadias
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12">
        <div className="container" ref={testimonialsRef}>
          <h2 
            className={cn(
              "text-2xl md:text-3xl font-bold text-center mb-10 transition-all duration-700",
              testimonialsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            O que dizem os tutores
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto perspective-1000">
            {testimonials.map((testimonial, i) => (
              <div 
                key={i} 
                className={cn(
                  "bg-muted rounded-xl p-6 transition-all duration-700",
                  "hover:shadow-xl hover:-translate-y-2 hover-tilt cursor-pointer",
                  testimonialsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
                )}
                style={{ 
                  transitionDelay: `${i * 150}ms`,
                  transformStyle: "preserve-3d"
                }}
              >
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: testimonial.rating }).map((_, j) => (
                    <Star 
                      key={j} 
                      className={cn(
                        "w-4 h-4 fill-forest text-forest transition-all duration-300",
                        testimonialsInView && "animate-scale-bounce"
                      )}
                      style={{ animationDelay: `${i * 150 + j * 100}ms` }}
                    />
                  ))}
                </div>
                <p className="text-foreground mb-4">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-forest/20"
                    />
                    <div className="absolute inset-0 rounded-full gradient-border-spin opacity-0 hover:opacity-100 transition-opacity" style={{ padding: "2px" }}>
                      <div className="w-full h-full rounded-full bg-muted" />
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.pet}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
